import { nextTick } from 'vue';
import { URL_SEARCH } from '../../constants';
import { HANDLE_CLICK, RECORD_CLICK } from '../actions/_types';
import { CLOSE_FILTERS, OPEN_FILTERS, TOGGLE_FILTER_MENU } from '../mutations/_types';

const FILTER_MODAL_ID = 'modal-filters';
const appliedRefinementsSection = 'applied refinements';
const clearAllLabel = 'Clear all';
const resultsId = 'er-results';

const noop = () => {};

const getFilterTitle = (title, items) => {
  const count = items.reduce((c, item) => c + (item.selected ? 1 : 0), 0);
  if (count) return `${title}\u00a0(${count})`;
  return title;
};

const mobileScroll = (name, mobile, expanded) => {
  const modal = document.getElementById(FILTER_MODAL_ID);
  const filter = document.getElementById(`filter-${name}`);
  const button = modal.querySelector('ul li button');
  if (!modal || !filter || !button) return;
  const filterTop = filter.getBoundingClientRect().top;
  if (mobile && !expanded) {
    const scrollPos = modal.scrollTop + filterTop - button.offsetHeight;
    setTimeout(() => {
      modal.scrollTo({ top: scrollPos, behavior: 'smooth' });
    }, 100);
  }
};

export default ({ pageParam, filtersCollapsable }) =>
  (state, getters, rootState) =>
  ({
    [HANDLE_CLICK]: handleClick,
    [RECORD_CLICK]: recordClick,
    [TOGGLE_FILTER_MENU]: toggleFilterMenu,
    [OPEN_FILTERS]: openFilters,
    [CLOSE_FILTERS]: closeFilters,
  }) => {
    const {
      filterModalOpened,
      pageTitle: title,
      breadcrumbsArr,
      searchObj,
      appliedRefinements: refinements,
      facets,
    } = state;
    const { filterExpanded } = getters;
    const { pathname, search: searchParams, isMobile } = rootState.root;
    const { totalResults, totalPages, currentPage } = searchObj;

    if (!facets || !facets.length) return null;

    // tree of applied refinements reduced to an array
    const appliedRefinements = refinements.reduce(function flatten(result, ar) {
      return [
        ...result,
        ...(ar.children && ar.children.length ? ar.children.reduce(flatten, []) : [ar]),
      ];
    }, []);

    return {
      class: 'filters--inner-modals',
      resultsId,
      heading: {
        title,
        count: totalResults,
      },
      useCdrBreadcrumbs: true,
      breadcrumbs: breadcrumbsArr.map((breadcrumb) => ({
        label: breadcrumb.label,
        key: breadcrumb.link,
        link: {
          href: breadcrumb.canonicalLink,
          onCick: (event) => {
            handleClick({
              event,
              to: breadcrumb.forward ? breadcrumb.link : breadcrumb.browserLink,
              analytics: {
                section: 'breadcrumbs',
                linkText: breadcrumb.label,
              },
              // breadcrumb.forward indicates that the breadcrumb isn't a search page
              // in general this means linking to a brand page
              // e.g. for /b/kelty/c/all - first breadcrumb links to /b/kelty
              external: breadcrumb.forward,
            });
          },
        },
      })),
      filterMenus: facets.map(
        ({ title: facetTitle, name: facetName, items: facetItems }, index) => {
          const facetDisabled = !facetItems.length;

          return {
            name: facetName,
            title: getFilterTitle(facetTitle, facetItems),
            key: facetName,
            id: `filter-${facetName}`,
            class: facetDisabled && 'filters--disabled',
            collapsable: filtersCollapsable,
            toggle: filtersCollapsable
              ? () => {
                  if (facetDisabled) return;

                  mobileScroll(facetName, isMobile, filterExpanded(facetName));
                  toggleFilterMenu(facetName);
                  recordClick({
                    section: 'facets',
                    linkText: `${facetName}:${filterExpanded(facetName) ? 'expand' : 'collapse'}`,
                  });
                }
              : noop,
            expanded: !facetDisabled && (!filtersCollapsable || filterExpanded(facetName)),
            items: facetItems.map(function f(i, itemIndex) {
              return {
                name: i.name,
                label: i.count > 0 ? `${i.label}\u00a0(${i.count})` : i.label,
                count: i.count,
                key: i.queryString,
                selected: i.selected,
                highlight: i.highlight,
                children: (i.children || []).map(f),
                link: {
                  attrs: {
                    href: i.canonicalLink,
                    'data-filter': i.name,
                  },
                  on: {
                    click: (event) => {
                      handleClick({
                        event,
                        to: i.browserLink,
                        analytics: {
                          section: 'facets',
                          linkText: `${facetTitle}:${i.label}:${
                            i.selected ? 'deselect' : 'select'
                          }`,
                          facetGroupIndex: `${index}`,
                          facetIndex: `${itemIndex}`,
                        },
                      });
                    },
                  },
                },
              };
            }),
          };
        },
      ),
      pagination: {
        totalPages,
        currentPage,
        getLink: (n) => {
          const nextQuery = new URLSearchParams(searchParams);
          if (n === 1) nextQuery.delete(pageParam);
          else nextQuery.set(pageParam, n);
          const queryString = nextQuery.toString();
          const href = queryString ? `?${queryString}` : pathname;
          return {
            href,
            onClick: async (event) => {
              await nextTick();

              // Need to force a reflow in order to
              // ensure smooth scroll happens every time in Firefox
              void window.scrollY; // eslint-disable-line no-void
              window.scrollTo({ top: 0, behavior: 'smooth' });

              handleClick({
                event,
                to: href,
                analytics: {
                  section: 'pagination',
                  linkText: `${n}`,
                },
              });
            },
          };
        },
      },
      filterModal: {
        id: FILTER_MODAL_ID,
        opened: filterModalOpened,
        open: () => {
          openFilters();
          recordClick({
            section: 'filters',
            linkText: 'open',
          });
        },
        close: () => {
          closeFilters();
          recordClick({
            section: 'filters',
            linkText: 'close',
          });
        },
        loading: false,
        closeButtonText: appliedRefinements.length
          ? // Text gives some feedback if any filters were applied ...
            // otherwise it's just "close filters"
            `Show ${totalResults} Results`
          : 'Close Filters',
      },
      appliedRefinements: {
        items: appliedRefinements.map((ar) => ({
          label: ar.label,
          value: ar.value,
          key: `${encodeURIComponent(ar.value)}=${encodeURIComponent(ar.label)}`,
          link: {
            href: ar.canonicalLink,
            onClick: (event) => {
              handleClick({
                event,
                to: ar.browserLink,
                analytics: {
                  section: appliedRefinementsSection,
                  linkText: ar.label,
                },
              });
            },
          },
        })),
        clearAll: {
          label: clearAllLabel,
          link: {
            href: URL_SEARCH,
            click: (event) => {
              handleClick({
                event,
                to: URL_SEARCH,
                analytics: {
                  section: appliedRefinementsSection,
                  linkText: clearAllLabel,
                },
              });
            },
          },
        },
      },
    };
  };
